import React from 'react';
import SubPage from '../components/site/SubPage';
import {
  ClosingBrand,
  PageSection,
  PageTitleBox,
  SectionHeading,
} from '../components/site/SubPageParts';

const IMG = '/images/site/family-impact';

const STRONGHOLD = [
  {
    term: 'Emotional Investment',
    body: 'Victims don’t see the manipulation; the scammer is a “friend” or “partner” who understands and cares about them.',
  },
  {
    term: 'Psychologically',
    body: 'It’s difficult coming to grips that you’re the victim of a scam — you’ve been duped. It strikes at the core of who we are as a person; it’s incredibly painful. By ignoring the warning signs or dismissing contradictory evidence, we can avoid pain and embarrassment.',
  },
  {
    term: 'Psychology of Reasoning',
    body: 'Scammers know how you think. They are skilled at handling your doubts and objections, sidestepping suspicion, and convincing their victims to actively work against their own best interests.',
  },
  {
    term: 'Self-Worth',
    body: 'Everyone believes we have value and seeks approval from others, and scammers corrupt that emotion, using it against their targets.',
  },
];

/*
 * "Connecting the dots" capabilities. The mockup's captions were truncated
 * mid-sentence; each is completed to the obvious reading.
 */
const DOTS = [
  {
    title: 'Skills',
    body: 'Skills and practical understanding to make an informed, risk-based decision.',
    icon: 'head.png',
  },
  {
    title: 'Analysis',
    body: 'Piecing together separate bits of information to make an informed decision.',
    icon: 'magnifying-glass.png',
  },
  {
    title: 'Anticipation',
    body: 'Perceiving, understanding and anticipating scammer actions, and making informed decisions.',
    icon: 'head2.png',
  },
];

const FamilyImpact = () => (
  <SubPage title="Family Impact">
    <PageTitleBox>The unforgiving reality.</PageTitleBox>

    {/* ---------------- Opening pair ---------------- */}
    <PageSection className="pt-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <img
          src={`${IMG}/mother-and-daughter.jpeg`}
          alt="A daughter comforting her mother outdoors"
          className="h-[220px] w-full object-cover small:aspect-[332/165] small:h-auto"
        />
        <img
          src={`${IMG}/eye.jpeg`}
          alt="A close-up of an eye in shadow"
          className="h-[220px] w-full object-cover small:aspect-[332/165] small:h-auto"
        />
      </div>

      <SectionHeading className="mt-10">It was all a lie.</SectionHeading>
      <img
        src={`${IMG}/all-a-lie.jpeg`}
        alt="Two sculpted hearts among scattered petals"
        className="mt-5 h-[220px] w-full object-cover small:aspect-[336/95] small:h-auto"
      />
    </PageSection>

    {/* ---------------- The Confrontation ---------------- */}
    <PageSection className="pt-12">
      <SectionHeading>The Confrontation.</SectionHeading>
      <img
        src={`${IMG}/confrontation.jpeg`}
        alt="Two older men in heated disagreement"
        className="mt-5 h-[240px] w-full object-cover small:aspect-[168/55] small:h-auto"
      />
      <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-black md:text-[17px]">
        <p>
          Trying to convince someone they&rsquo;re falling for a scam can be fruitless &mdash;
          head-scratching, frustrating, puzzling. Perhaps it&rsquo;s a family friend, or a parent
          who believes they&rsquo;ve found their soulmate or been pitched a &ldquo;once in a
          lifetime investment&rdquo; opportunity. In spite of your best efforts, you can&rsquo;t
          break through &mdash; they&rsquo;re defiant. Trying to convince them seems pointless;
          they know what they are doing, and you just don&rsquo;t get it.
        </p>
        <p>
          Arguing facts and statistics, and sharing similar stories, seems like a waste of time.
          Falling for a scam &mdash; that happens to other people, not me. They are dismissive and
          disinterested. The scammer has a stronghold. Here&rsquo;s why:
        </p>
      </div>

      <ul className="mt-5 space-y-3">
        {STRONGHOLD.map((item) => (
          <li key={item.term} className="flex gap-3 text-[15px] leading-snug text-black md:text-[17px]">
            <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 flex-shrink-0 rounded-full bg-black" />
            <span>
              <strong className="font-semibold">{item.term}:</strong> {item.body}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-black md:text-[17px]">
        <p>
          Scammers are predators, abusers, and master manipulators, while seniors by contrast are
          honest, decent, good-natured people &mdash; and that is what makes them a perfect target.
        </p>
        <p>
          These digital violators zero in and exploit seniors&rsquo; trusting nature. Most seniors
          were raised in a different time, with different values. While these are outstanding
          qualities, it also makes them more vulnerable to scammers.
        </p>
      </div>
    </PageSection>

    {/* ---------------- The most difficult choice ---------------- */}
    <PageSection className="pt-12">
      <SectionHeading>The most difficult choice.</SectionHeading>
      <img
        src={`${IMG}/difficult-choice.jpeg`}
        alt="A woman face down on a sofa, overwhelmed"
        className="mt-5 h-[240px] w-full object-cover small:aspect-[16/5] small:h-auto"
      />
      <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-black md:text-[17px]">
        <p>
          Scamming is a family affair; it victimizes mothers, fathers, sons, daughters, husbands,
          wives and grandchildren &mdash; no one is spared. Watching a loved one victimized by a
          scammer feels like watching a human train wreck unfold in real time with no way to stop
          it. It&rsquo;s a gut punch. It&rsquo;s a tough day.
        </p>
        <p>
          Sadly, not everyone can be saved. That&rsquo;s the hard truth &mdash; they are past the
          point of no return, and it&rsquo;s out of your control. That can be frustrating,
          especially if they&rsquo;re being scammed out of their life savings and you&rsquo;re a
          bystander in this real-life tragedy with devastating consequences.
        </p>
      </div>
    </PageSection>

    {/* ---------------- The Aftermath ---------------- */}
    <PageSection className="pt-12">
      <SectionHeading>The Aftermath.</SectionHeading>
      <img
        src={`${IMG}/aftermath.jpeg`}
        alt="A shattered ceramic pot spilled across a floor"
        className="mt-5 h-[240px] w-full object-cover small:aspect-[16/5] small:h-auto"
      />
      <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-black md:text-[17px]">
        <p>
          Being a victim of a scam is life altering; the impact goes beyond just financial loss
          &mdash; it leaves psychological and emotional gashes.
        </p>
        <p>
          Victims have severe emotional trauma and trust issues. Your faith in humanity has been
          obliterated &mdash; everything was a lie, an illusion, always about the money. In the
          wake of a scam, victims second-guess their judgement, are unable to trust, and face
          social isolation and a pervasive sense of vulnerability.
        </p>
        <p>
          Then there&rsquo;s the financial stress and anxiety. Victims may experience sleepless
          nights, constant worry about their financial future, and a gnawing sense of insecurity.
          It&rsquo;s like living with a dark cloud hanging over your head, never knowing when the
          next financial storm might hit.
        </p>
      </div>
    </PageSection>

    {/* ---------------- Early Intervention ---------------- */}
    {/* Drawn in the mockup as an open bracket rather than a closed box. The rule
        runs from the heading across the top and down the right, then turns back
        along the bottom only as far as "Connecting the dots" -- nothing protrudes
        to its left. There is no left edge either: the photo sits in that gap, and
        the box is held tall enough that its lower edge lands level with the foot
        of the photo. Below md the whole conceit drops to a plain stack. */}
    <PageSection className="pt-12 md:mb-28">
      <div className="md:relative">
        <SectionHeading className="md:absolute md:left-0 md:top-0 md:z-10 md:-translate-y-1/2 md:bg-white md:pr-6">
          Early Intervention.
        </SectionHeading>

        <img
          src={`${IMG}/early-intervention.jpeg`}
          alt="A woman working at a laptop"
          className="mt-5 h-[240px] mini:h-[545px] w-full object-cover md:absolute md:left-0 md:top-12 md:z-10 md:mt-0 md:h-[160px] md:w-[28%] lg:h-[300px]"
        />

        {/* Top and right only -- the bottom rule is drawn with the heading below,
            so it can stop rather than run the full width. */}
        <div className="mt-6 space-y-4 border border-black/20 p-5 text-[15px] leading-relaxed text-black md:mt-0 md:min-h-[348px] md:border-b-0 md:border-l-0 md:py-12 md:pl-[34%] md:pr-10 md:text-[17px]">
          <p>
            Today&rsquo;s current crop of security tools are not designed to meet the complexity of
            social engineering scams.
          </p>
          <p>
            No / SCAM&rsquo;s MoSAIC PSYOPS assessment changes that, empowering you with private
            assessment tools designed specifically for social engineering scams. This
            first-generation private social engineering assessment tool alerts you to potential
            danger.
          </p>
        </div>

        {/* The heading first, then the rule running out to meet the right border. */}
        <div className="mt-10 md:absolute md:bottom-0 md:left-[40%] md:right-0 md:z-10 md:mt-0 md:flex md:translate-y-1/2 md:items-center">
          <h3 className="text-center text-[24px] font-bold text-black md:whitespace-nowrap md:bg-white md:pr-5 md:text-[32px]">
            Connecting the dots.
          </h3>
          <span aria-hidden="true" className="hidden h-px flex-1 bg-black/20 md:block" />
        </div>
      </div>
    </PageSection>

    {/* ---------------- Connecting the dots ---------------- */}
    <PageSection className="pb-12 pt-12 md:pb-24 md:pt-0">
      {/* The cube straddles the top border and the AI mark the bottom, per
          the supplied screenshot. Each carries the page's white ground and a
          little horizontal padding, which masks the rule running behind it. */}
      <div className="relative mt-10 border border-black/20 px-6 pb-16 pt-16 md:mt-12 md:px-10 md:pb-24 md:pt-24">
        <img
          src={`${IMG}/box.png`}
          alt=""
          aria-hidden="true"
          className="absolute left-1/2 top-0 h-[70px] w-auto -translate-x-1/2 -translate-y-1/2 bg-white px-4 object-contain md:h-[86px]"
        />

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
          {DOTS.map((dot) => (
            <div key={dot.title} className="text-center">
              {/* Supplied artwork replaces the placeholder marks that stood in
                  for these three. */}
              <img
                src={`${IMG}/${dot.icon}`}
                alt=""
                aria-hidden="true"
                className="mx-auto h-[70px] w-auto object-contain md:h-[86px]"
              />
              <p className="mt-3 text-[15px] leading-snug text-black md:text-[16px]">{dot.body}</p>
            </div>
          ))}
        </div>

        <img
          src="/images/site/neurodivergent-explained/ai-cloud-blue.png"
          alt="NuVola AI"
          className="absolute bottom-0 left-1/2 h-[54px] w-auto -translate-x-1/2 translate-y-1/2 bg-white px-4 object-contain md:h-[70px]"
        />
      </div>
    </PageSection>

    {/* Footer per the supplied screenshot. The deck reads "A new dimensions";
        set in the singular here. */}
    <ClosingBrand tagline="A new dimension in prevention." />
  </SubPage>
);

export default FamilyImpact;
